import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Card, Button, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../salesTaxLedgerRecordSlice";
import { fetchSalesTaxLedger } from "../salesTaxLedgerThunk";
import { fetchTaxAccs } from "../../../../store/commonThunks/lookups";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";
import { getVatQuarters } from "../../../../lib/vatStagger";
import { useLocation, useSearchParams } from "react-router-dom";

const { Option } = Select;

const SalesTaxLedgerFilters = ({ messageApi }) => {
  const dispatch   = useDispatch();
  const { filters } = useSelector((state) => state.salesTaxLedger);
  const { taxAccs } = useSelector((state) => state.lookups);
  const authInfo    = useSelector((state) => state.auth?.auth_info);


  const { quarters: storeQuarters } = useSelector((state) => state.vatFiling);

  const isUAE        = authInfo?.country?.toLowerCase() !== "india";
  const staggerMonth = authInfo?.vat_stagger_month;
  const deadlineDay  = authInfo?.vat_deadline_day || 28;

  // Use store quarters if available, fallback to computed only if store is empty
  const quarters = storeQuarters.length > 0
    ? storeQuarters
    : (isUAE && staggerMonth)
      ? getVatQuarters(staggerMonth, authInfo?.vat_deadline_day || 28)
      : [];
  const [selectedQuarterNum, setSelectedQuarterNum] = useState(null);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  useDefaultDateRange(setFilters, filters, "today");

  useEffect(() => {
    dispatch(fetchTaxAccs());
  }, [dispatch]);

  // Drill-down from Balance Sheet: /reports/tax-ledger/<toDate>?fromDate=<fromDate>
  // Pre-fills the period; user selects the VAT/GST account and runs the report.
  useEffect(() => {
    const path = pathname.split("/")[3];
    if (path) {
      const fromDate = searchParams.get("fromDate");
      const patch = { toDate: path };
      if (fromDate) patch.fromDate = fromDate;
      dispatch(setFilters(patch));
    }
  }, []);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  // Quarter selected → auto fill fromDate and toDate
  const handleQuarterChange = (quarterNum) => {
    setSelectedQuarterNum(quarterNum);
    if (!quarterNum) return;
    const q = quarters.find((q) => q.quarterNum === quarterNum);
    if (q) {
      dispatch(setFilters({
        fromDate: q.fromDate,
        toDate:   q.toDate,
      }));
    }
  };

  // Manual date change → clear quarter selection
  const handleDateChange = (fromDate, toDate) => {
    setSelectedQuarterNum(null);
    handleFilterChange("fromDate", fromDate);
    handleFilterChange("toDate",   toDate);
  };

  const handleReport = () => {
    if (!filters.accId) {
      messageApi.warning("Select Account");
      return;
    }
    dispatch(fetchSalesTaxLedger(filters)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} align="middle">

        {/* Account */}
        <Col xs={24} sm={12} md={6}>
          <label>Account</label>
          <SearchListAutoComplete
            optionsList={(taxAccs || [])
              .filter((acc) => {
                const name = acc.acc_name.toLowerCase();
                return name.includes("vat") || name.includes("gst");
              })
              .map((acc) => ({
                sale_id:      acc.acc_id,
                display_text: acc.acc_name,
              }))}
            onSelect={(obj) => handleFilterChange("accId", obj?.sale_id)}
            placeholder="Tax Account"
            showAddNew={false}
          />
        </Col>

        {/* Quarter picker — only for UAE with stagger configured */}
        {isUAE && quarters.length > 0 && (
          <Col xs={24} sm={12} md={6}>
            <label>VAT Quarter</label>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              placeholder="Select quarter"
              allowClear
              value={selectedQuarterNum}
              onChange={handleQuarterChange}
              onClear={() => setSelectedQuarterNum(null)}
            >
              {quarters.map((q) => (
                <Option key={q.quarterNum} value={q.quarterNum}>
                  <span
                    style={{
                      display:        "flex",
                      justifyContent: "space-between",
                      alignItems:     "center",
                    }}
                  >
                    <span>{q.label} ({q.period})</span>
                    {q.computedStatus === "overdue" && (
                      <span style={{
                        fontSize: 10, color: "#ff4d4f",
                        marginLeft: 8, fontWeight: 500
                      }}>
                        OVERDUE
                      </span>
                    )}
                    {q.computedStatus === "due" && (
                      <span style={{
                        fontSize: 10, color: "#faad14",
                        marginLeft: 8, fontWeight: 500
                      }}>
                        DUE
                      </span>
                    )}
                  </span>
                </Option>
              ))}
            </Select>
          </Col>
        )}

        {/* Date range — syncs with quarter picker */}
        <CommonDateRangeFilter
          filters={filters}
          onChange={handleDateChange}
        />

        {/* Report button */}
        <Col
          xs={24} sm={12} md={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            block
            style={{ height: 35.19 }}
          >
            Report
          </Button>
        </Col>

      </Row>
    </Card>
  );
};

export default SalesTaxLedgerFilters;